import type {
  DiamModifierValues,
  DiamMyTeamRoles,
  DiamRoleAccess,
} from '../../diamApi';
import type {
  AccessDashboardData,
  DashboardApplicationGroup,
  DashboardRole,
} from './types';

const UNGROUPED_APPLICATION_ID = 'ungrouped';
const UNGROUPED_APPLICATION_NAME = 'Other';

export function flattenModifiers(modifiers?: DiamModifierValues): string[] {
  if (!modifiers) {
    return [];
  }

  return Object.entries(modifiers)
    .filter(([, value]) => Boolean(value))
    .map(([key, value]) => `${key}: ${value}`);
}

function toDashboardRole(
  role: DiamRoleAccess,
  applicationId: string,
  canRequestAccess: boolean,
): DashboardRole {
  return {
    id: role.id,
    applicationId,
    name: role.name,
    description: role.description ?? '',
    permissions: role.permissions ?? [],
    modifiers: flattenModifiers(role.modifiers),
    ...(canRequestAccess ? { canRequestAccess: true } : {}),
    ...(role.requestPending ? { isAwaitingApproval: true } : {}),
  };
}

function groupRoles(
  roles: readonly DiamRoleAccess[],
  canRequestAccess: boolean,
): DashboardApplicationGroup[] {
  const groups = new Map<string, DashboardApplicationGroup>();

  for (const role of roles) {
    const applicationId = role.applicationContextId ?? UNGROUPED_APPLICATION_ID;
    const applicationName =
      role.applicationContextName ?? UNGROUPED_APPLICATION_NAME;
    const existingGroup = groups.get(applicationId);

    if (existingGroup) {
      existingGroup.roles.push(
        toDashboardRole(role, applicationId, canRequestAccess),
      );
      continue;
    }

    groups.set(applicationId, {
      application: {
        id: applicationId,
        name: applicationName,
        type: '',
      },
      roles: [toDashboardRole(role, applicationId, canRequestAccess)],
    });
  }

  return Array.from(groups.values());
}

export function mapMyTeamRoles(dto: DiamMyTeamRoles): AccessDashboardData {
  return {
    teamName: dto.teamName,
    granted: groupRoles(dto.grantedRoles ?? [], false),
    available: groupRoles(dto.availableRoles ?? [], true),
  };
}
